import { TLiftedFormModel } from '../../model/form.model';
import {
  TREQUEST_UPDATE_MACHINE,
  TRESPONSE_MACHINE_LIST,
  TRESPONSE_UPDATE_MACHINE_FIELD_DROPDOWN,
} from '@monorepo/types';
import moment from 'moment';
import { FORM_MODIFIER } from '../../model/form.modifier';
import { PIPELINE_FORM } from '../../model/form.pipeline';
import { TTableVirtualScrollConfig } from '../../../table/models/table.type';

const VIRTUAL_SCROLL_DEFAULT_CONFIG: TTableVirtualScrollConfig = {
  itemSize: 40,
  minBufferPx: window.screen.height / 2,
  maxBufferPx: window.screen.height / 2,
};

export type TUpdateMachineFormModel = {
  machineType: [string];
  ownershipType: TREQUEST_UPDATE_MACHINE['details']['ownershipType'];
  machineCategory: [string];
  ownedAt?: number;
  machineSubCategory?: [string];
  preventiveSchedule: number;
  brand: [string];
  preventiveLastScheduledAt?: number;
  machineModelNumber?: string;
  manufacturerSerialNumber?: string;
  lubricantType?: string;
  lubricationFrequency?: number;
};

export const UPDATE_MACHINE_FORM_MODEL = {
  createModel: (
    machine: TRESPONSE_MACHINE_LIST['machines'][number]['machine'],
    dropdownList: TRESPONSE_UPDATE_MACHINE_FIELD_DROPDOWN,
  ): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
            value: [machine.machineType],
            options: dropdownList.machineTypes.map((mT) => ({ value: mT, displayName: mT })),
            virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG,
          },
          metadata: {
            keyName: 'machineType',
            label: 'update_machine.MachineType',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'DROPDOWN',
            value: machine.ownershipType,
            options: dropdownList.ownershipTypes.map((oT) => ({ value: oT, displayName: oT })),
          },
          metadata: {
            keyName: 'ownershipType',
            label: 'update_machine.TypeofPurchase',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
            value: [machine.machineCategory],
            options: dropdownList.machineCategories.map((mC) => ({ value: mC, displayName: mC })),
            virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG,
          },
          metadata: {
            keyName: 'machineCategory',
            label: 'update_machine.MachineCategory',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'date',
            value: machine.ownedAt ? moment(machine.ownedAt).format('YYYY-MM-DD') : undefined,
            dateType: 'DATETIME',
          },
          metadata: {
            keyName: 'ownedAt',
            label: 'update_machine.PurchaseDate',
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
            value: [machine.machineSubCategory || ''],
            options: dropdownList.machineSubCategories.map((mSC) => ({ value: mSC, displayName: mSC })),
            virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG,
          },
          metadata: {
            keyName: 'machineSubCategory',
            label: 'update_machine.MachineSubCategory',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            value: machine.preventiveSchedule,
            min: 1,
          },
          metadata: {
            keyName: 'preventiveSchedule',
            label: 'update_machine.PreventiveSchedule',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
            value: [machine.brand],
            options: dropdownList.brands.map((b) => ({ value: b, displayName: b })),
            virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG,
          },
          metadata: {
            keyName: 'brand',
            label: 'update_machine.Brand',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'date',
            value: machine.preventiveLastScheduledAt
              ? moment(machine.preventiveLastScheduledAt).format('YYYY-MM-DD')
              : undefined,
            dateType: 'DATETIME',
          },
          metadata: {
            keyName: 'preventiveLastScheduledAt',
            label: 'update_machine.PreventiveLastScheduleDate',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.machineModelNumber,
          },
          metadata: {
            keyName: 'machineModelNumber',
            label: 'update_machine.MachineModelNo.',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.machineStatus,
          },
          metadata: {
            keyName: 'machineStatus',
            label: 'update_machine.MachineStatus',
            isReadonly: true,
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.manufacturerSerialNumber,
          },
          metadata: {
            keyName: 'manufacturerSerialNumber',
            label: 'update_machine.Manufacturer’sSerialNo.',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.machineQRStatus,
          },
          metadata: {
            keyName: 'machineQRStatus',
            label: 'Printed Status',
            isReadonly: true,
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.lubricantType,
          },
          metadata: {
            keyName: 'lubricantType',
            label: 'update_machine.LubricantOilType',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'text',
            value: machine.factorySerialNumber,
          },
          metadata: {
            keyName: 'factorySerialNumber',
            label: 'update_machine.FactorySerialNo.',
            isReadonly: true,
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            value: machine.lubricationFrequency,
            min: 1,
          },
          metadata: {
            keyName: 'lubricationFrequency',
            label: 'update_machine.LubricationFrequency',
          },
        },
      ],
    });
  },

  identity: (): TLiftedFormModel => {
    return {
      inputs: [],
    };
  },

  onParentSave: (formModel: TLiftedFormModel): TLiftedFormModel => {
    return FORM_MODIFIER.markTouchedInputs(formModel);
  },
};
